// royco-risk/frontend/src/components/integrated/SessionContext.js
import { createContext, useContext, useReducer, useCallback } from 'react';

// ── Default State ──

const DEFAULT_MARKET = {
  status: 'incomplete',
  ydm: { version: 'V2', y0: 0.05, yT: 0.05, y100: 0.40, speed: 80 },
  params: { minCoverage: 0.10, recoveryDuration: 7, stFee: 0.10, jtFee: 0, ysFee: 0.45 },
  underlying: { source: 'apy', dataKey: null, yield: 0.05, utilization: 0.90 },
  liquidation: { finalizedLossThreshold: 0.05, selfLiquidationBonus: 0.02 },
};

export const INITIAL_STATE = {
  version: '1.0.0',
  markets: {},
  dsv: {
    status: 'incomplete',
    allocations: {},
    simParams: { reversionDays: 7, liquidationBonusMode: 'optimistic', liquidationRedemptionRate: 0 },
  },
  royeth: {
    status: 'incomplete',
    aaveLTV: 0.39, liqThreshold: 0.83,
    useHistoricRates: true,
    stakingYield: 0.027, aaveSupply: 0.0004, usdcBorrow: 0.056,
    ltvMode: 'dynamic', maxDsvRedemption: 3,
    harvestEnabled: true, harvestIntervalDays: 7,
  },
  royethV2: {
    status: 'incomplete',
    aaveLTV: 0.39, dsvLTV: 0.50, liqThreshold: 0.83,
    useHistoricRates: true,
    stakingYield: 0.027, aaveSupply: 0.0004, usdcBorrow: 0.056, dsvBorrow: 0.056, dsvBorrowSpread: 0,
    ltvMode: 'dynamic', maxDsvRedemption: 3,
    harvestEnabled: true, harvestIntervalDays: 7,
    sroywstETHLTV: 0.50, sroywstETHBorrowPremium: 0.005, sroywstETHAbsoluteRate: false, sroywstETHBorrowRate: 0.032,
    dsvBorrowAbsoluteRate: false, dsvBorrowAbsolute: 0.056,
    v1LTV: null,
  },
  overview: {
    eigenETH: { morphoLTV: 0.66, ethBorrowPremium: 0.005, liqThreshold: 0.85, depeg: false, depegPct: 0.10 },
    vanilla: { baseEthBorrow: 0.022, supplyYield: 0.0002 },
  },
};

// ── Reducer ──

function sessionReducer(state, action) {
  switch (action.type) {
    case 'ADD_MARKET': {
      const { name, config } = action;
      return {
        ...state,
        markets: { ...state.markets, [name]: { ...DEFAULT_MARKET, ...config } },
      };
    }
    case 'UPDATE_MARKET': {
      const { name, patch } = action;
      const existing = state.markets[name] || DEFAULT_MARKET;
      return {
        ...state,
        markets: {
          ...state.markets,
          [name]: {
            ...existing,
            ...patch,
            ydm: { ...existing.ydm, ...(patch.ydm || {}) },
            params: { ...existing.params, ...(patch.params || {}) },
            underlying: { ...existing.underlying, ...(patch.underlying || {}) },
            liquidation: { ...existing.liquidation, ...(patch.liquidation || {}) },
          },
        },
      };
    }
    case 'REMOVE_MARKET': {
      const { [action.name]: _, ...rest } = state.markets;
      return { ...state, markets: rest };
    }
    case 'SET_MARKET_STATUS': {
      const { name, status } = action;
      return {
        ...state,
        markets: { ...state.markets, [name]: { ...state.markets[name], status } },
      };
    }
    case 'UPDATE_DSV': {
      return {
        ...state,
        dsv: {
          ...state.dsv,
          ...action.patch,
          allocations: { ...state.dsv.allocations, ...(action.patch.allocations || {}) },
          simParams: { ...state.dsv.simParams, ...(action.patch.simParams || {}) },
        },
      };
    }
    case 'UPDATE_DSV_ALLOCATION': {
      const { name, patch } = action;
      return {
        ...state,
        dsv: {
          ...state.dsv,
          allocations: {
            ...state.dsv.allocations,
            [name]: { ...state.dsv.allocations[name], ...patch },
          },
        },
      };
    }
    case 'SET_DSV_STATUS': {
      return { ...state, dsv: { ...state.dsv, status: action.status } };
    }
    case 'UPDATE_ROYETH': {
      return { ...state, royeth: { ...state.royeth, ...action.patch } };
    }
    case 'SET_ROYETH_STATUS': {
      return { ...state, royeth: { ...state.royeth, status: action.status } };
    }
    case 'UPDATE_ROYETH_V2': {
      return { ...state, royethV2: { ...state.royethV2, ...action.patch } };
    }
    case 'SET_ROYETH_V2_STATUS': {
      return { ...state, royethV2: { ...state.royethV2, status: action.status } };
    }
    case 'UPDATE_OVERVIEW': {
      return {
        ...state,
        overview: {
          ...state.overview,
          eigenETH: { ...state.overview.eigenETH, ...(action.patch.eigenETH || {}) },
          vanilla: { ...state.overview.vanilla, ...(action.patch.vanilla || {}) },
        },
      };
    }
    case 'IMPORT_STATE': {
      const imported = action.state;
      return {
        ...INITIAL_STATE,
        ...imported,
        version: INITIAL_STATE.version,
        markets: imported.markets || state.markets,
        dsv: { ...INITIAL_STATE.dsv, ...(imported.dsv || {}) },
        royeth: { ...INITIAL_STATE.royeth, ...(imported.royeth || {}) },
        royethV2: { ...INITIAL_STATE.royethV2, ...(imported.royethV2 || {}) },
        overview: {
          eigenETH: { ...INITIAL_STATE.overview.eigenETH, ...(imported.overview?.eigenETH || {}) },
          vanilla: { ...INITIAL_STATE.overview.vanilla, ...(imported.overview?.vanilla || {}) },
        },
      };
    }
    case 'RESET': {
      return { ...INITIAL_STATE };
    }
    default:
      return state;
  }
}

// ── Context ──

const SessionContext = createContext(null);
const SessionDispatchContext = createContext(null);

export function SessionProvider({ children }) {
  const [state, dispatch] = useReducer(sessionReducer, INITIAL_STATE);

  const exportState = useCallback(() => {
    return { ...state, exportedAt: new Date().toISOString() };
  }, [state]);

  const importState = useCallback((json) => {
    try {
      const parsed = typeof json === 'string' ? JSON.parse(json) : json;
      dispatch({ type: 'IMPORT_STATE', state: parsed });
      return { success: true };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }, []);

  return (
    <SessionContext.Provider value={{ state, exportState, importState }}>
      <SessionDispatchContext.Provider value={dispatch}>
        {children}
      </SessionDispatchContext.Provider>
    </SessionContext.Provider>
  );
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}

export function useSessionDispatch() {
  const ctx = useContext(SessionDispatchContext);
  if (!ctx) throw new Error('useSessionDispatch must be used within SessionProvider');
  return ctx;
}

export { DEFAULT_MARKET };
